import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { useSession } from 'auth/AuthProvider';
import { useWeeklyReportCreation } from 'hooks/useWeeklyReportCreation';
import { useReportParams } from 'hooks/useReportParams';

const WeeklyReportHandler: React.FC = () => {
    const location = useLocation();
    const session = useSession();
    const [error, setError] = React.useState('');

    // Extract parameters from URL
    const searchParams = new URLSearchParams(location.search);
    const classId = searchParams.get('class');
    const startDate = searchParams.get('start');
    const endDate = searchParams.get('end');
    // const accessToken = searchParams.get('access_token');

    // Clean up access token from URL after authentication
    // useTokenCleanup(!!accessToken, !!session_token);

    // Determine report parameters based on user role and URL params
    const reportParams = useReportParams({ classId, startDate, endDate });

    // Use unified report creation hook
    const { createReport, isLoading, isSuccess, isError } = useWeeklyReportCreation({
        onError: setError
    });

    // Auto-start report generation when authenticated and have valid params
    React.useEffect(() => {
        if (reportParams && startDate && endDate && 
            !isLoading && !isSuccess && !isError) {
            createReport({
                type: reportParams.type,
                id: reportParams.id,
                startDate,
                endDate
            });
        }
    }, [reportParams, startDate, endDate, createReport, isLoading, isSuccess, isError]);

    // Wait for user data to load before making decisions
    if (!session.uid) {
        return (
            <Box 
                display="flex" 
                flexDirection="column" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="400px"
                gap={3}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary" textAlign="center">
                    Loading...
                </Typography>
            </Box>
        );
    }

    // Validate parameters and redirect if invalid
    if (!reportParams) {
        const redirectTo = session.role === 'manager' ? "/organization-reports" : "/reports";
        return <Navigate to={redirectTo} replace />;
    }

    if (error) {
        return (
            <Box 
                display="flex" 
                flexDirection="column" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="400px"
                gap={2}
            >
                <Alert severity="error" sx={{ maxWidth: 500 }}>
                    {error}
                </Alert>
                <Typography 
                    component="a" 
                    href={session.role === 'manager' ? "/organization-reports" : "/reports"} 
                    sx={{ 
                        color: 'primary.main', 
                        textDecoration: 'underline',
                        cursor: 'pointer'
                    }}
                >
                    Go back to reports
                </Typography>
            </Box>
        );
    }

    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="400px"
            gap={3}
        >
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary" textAlign="center">
                Preparing your weekly report...
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 400 }}>
                {reportParams.type === 'organization' 
                    ? "This may take a few moments while we gather and analyze the data for your organization."
                    : "This may take a few moments while we gather and analyze the data for your class."
                }
            </Typography>
        </Box>
    );
};

export default WeeklyReportHandler;